import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../lib/api';

/**
 * MarketplaceModeContext
 *
 * Fetches and caches the platform's operating mode and public config for the current session.
 * Controls visibility of seller-facing UI elements and announcement bar across the entire app.
 *
 * API: GET /api/platform/mode  — marketplace mode + features
 * API: GET /api/platform/config/ — public config including announcement bar
 */
const MarketplaceModeContext = createContext(null);

export const useMarketplaceMode = () => {
  const ctx = useContext(MarketplaceModeContext);
  if (!ctx) {
    // Return safe defaults instead of crashing — prevents TypeError during initial render
    return {
      mode: 'single_vendor',
      isMultiVendor: false,
      features: { seller_registration: false, seller_dashboard: false, seller_listings: false },
      platformSellerName: 'zivu.eu',
      announcementBar: { enabled: true, items: [] },
      loading: true,
      refetch: async () => {},
    };
  }
  return ctx;
};

export const MarketplaceModeProvider = ({ children }) => {
  const [mode, setMode] = useState('single_vendor'); // default: one seller — no seller UI flash before /platform/mode resolves
  const [features, setFeatures] = useState({
    seller_registration: true,
    seller_dashboard: true,
    seller_listings: true,
  });
  const [platformSellerName, setPlatformSellerName] = useState('zivu.eu');
  const [announcementBar, setAnnouncementBar] = useState({
    enabled: true,
    items: [
      { text: 'We ship within 72 working hours', icon: 'truck', enabled: true },
      { text: '14-day returns', icon: 'refresh', enabled: true },
      { text: 'Delivery across the EU, UK & Norway', icon: 'package', enabled: true },
    ],
  });
  const [loading, setLoading] = useState(true);

  // localStorage cache key and TTL (10 minutes)
  const CACHE_KEY = 'zivu:marketplace_mode_v1';
  const CACHE_TTL_MS = 600_000;

  // Apply a cached mode snapshot to state (called on cache hit)
  const applyModeSnapshot = useCallback((snapshot) => {
    setMode(snapshot.mode || 'single_vendor');
    setPlatformSellerName(snapshot.platform_seller_name || 'zivu.eu');
    setFeatures(snapshot.features || {
      seller_registration: true,
      seller_dashboard: true,
      seller_listings: true,
    });
  }, []);

  const fetchMode = useCallback(async () => {
    try {
      const response = await apiClient.get('/platform/mode');
      const data = response.data;
      applyModeSnapshot(data);
      // Persist to localStorage with a timestamp for next page load
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
      } catch (_) { /* storage quota exceeded — ignore */ }
    } catch (error) {
      console.warn('Failed to fetch platform mode, defaulting to single_vendor:', error.message);
    }
  }, [applyModeSnapshot]);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await apiClient.get('/platform/config/');
      const data = response.data;
      if (data.announcement_bar_items || data.announcement_bar_enabled !== undefined) {
        setAnnouncementBar({
          enabled: data.announcement_bar_enabled !== undefined ? data.announcement_bar_enabled : true,
          items: data.announcement_bar_items || [],
        });
      }
    } catch (error) {
      console.warn('Failed to fetch platform config:', error.message);
    }
  }, []);

  useEffect(() => {
    // Check localStorage cache first — if fresh (<10 min old), hydrate state
    // immediately and skip the blocking XHR (fire it in the background instead).
    let cacheHit = false;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached.ts && Date.now() - cached.ts < CACHE_TTL_MS) {
          applyModeSnapshot(cached);
          cacheHit = true;
        }
      }
    } catch (_) { /* corrupt cache — ignore, fall through to XHR */ }

    if (cacheHit) {
      // Cache was fresh: unblock app shell immediately, refresh in background
      setLoading(false);
      // Fire both in background; if mode has changed it will update state + cache
      Promise.allSettled([fetchMode(), fetchConfig()]);
    } else {
      // No valid cache: fetch both, handle each independently so a config failure
      // doesn't prevent mode from hydrating
      Promise.allSettled([fetchMode(), fetchConfig()]).finally(() => setLoading(false));
    }
  }, [fetchMode, fetchConfig, applyModeSnapshot]);

  const isMultiVendor = mode === 'multi_vendor';

  const refetch = useCallback(async () => {
    await Promise.allSettled([fetchMode(), fetchConfig()]);
  }, [fetchMode, fetchConfig]);

  return (
    <MarketplaceModeContext.Provider
      value={{ mode, features, platformSellerName, isMultiVendor, announcementBar, loading, refetch }}
    >
      {children}
    </MarketplaceModeContext.Provider>
  );
};

export default MarketplaceModeProvider;
